import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, Minus, HelpCircle, MessageCircle } from 'lucide-react'

const FAQ = () => {
  const [openItems, setOpenItems] = useState({})

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  React.useEffect(() => {
    document.title = "Frequently Asked Questions - Shrijaa Outsourcing Consultants";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Find answers to common questions about Shrijaa Outsourcing Consultants staffing models, recruitment timelines, loan eligibility, interest rates, and financial products.");
    }

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/faq');

    const schemaId = 'faq-jsonld-schema';
    let script = document.getElementById(schemaId);
    if (!script) {
      script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.flatMap(cat => cat.questions).map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    };
    
    script.textContent = JSON.stringify(faqSchema);
    
    return () => {
      const activeScript = document.getElementById(schemaId);
      if (activeScript) {
        activeScript.remove();
      }
    };
  }, []);

  const faqs = [
    {
      category: 'General',
      questions: [
        {
          question: 'What services do you offer?',
          answer: 'We provide end-to-end manpower and staffing solutions, including: Housekeeping Services (men & women); Watchman & Security Services (including watchman with accommodation); Ward Boys & Girls for hospitals; Care Takers for home and elderly support; dedicated Security Services; Telecallers for outbound/inbound calling and campaigns; Accounting Jobs; Any Degree Jobs placement; Drivers; Delivery Boys; and Event & Function Services for weddings, functions, and events. Visit our Services page for full details on each offering.'
        },
        {
          question: 'How long has your company been in business?',
          answer: 'Shrijaa Outsourcing Consultants was established in 2024 and is committed to becoming a trusted manpower outsourcing partner across India.'
        },
        {
          question: 'What industries do you serve?',
          answer: 'We serve clients across Manufacturing Industries, Construction Companies, Hospitals and Healthcare, Corporate Offices, Retail and Shopping Chains, Logistics and Warehousing, Hospitality and Hotels, Educational Institutions, Banking & Financial Services, and Startups & SMEs.'
        },
        {
          question: 'Do you work with both job seekers and employers?',
          answer: 'Yes, we work with both job seekers looking for opportunities and employers seeking top talent. We facilitate connections between both parties.'
        }
      ]
    },
    {
      category: 'For Employers',
      questions: [
        {
          question: 'How quickly can you provide staff?',
          answer: 'Our response time varies by service type, but we typically can provide qualified staff within 72 hours for most positions. For urgent requirements, we can arrange staff even faster.'
        },
        {
          question: 'Do you provide accommodation for watchman staff?',
          answer: 'Yes, we provide accommodation for watchman staff as part of our service. This ensures reliable 24/7 security coverage for your premises. Please note that accommodation is available only for watchman services, not for general security services.'
        },
        {
          question: 'Do you conduct background checks?',
          answer: 'Yes, we conduct comprehensive background checks, reference verification, and skills assessments for all our candidates to ensure quality and reliability.'
        },
        {
          question: 'What are your fees?',
          answer: 'Our fees vary based on the service and position level. We offer transparent pricing with no hidden costs. Contact us for a customized quote based on your needs.'
        }
      ]
    },
    {
      category: 'For Job Seekers',
      questions: [
        {
          question: 'Is there a fee for job seekers?',
          answer: 'No, our services are completely free for job seekers. Employers pay our fees when we successfully place candidates.'
        },
        {
          question: 'How do I apply for positions?',
          answer: 'You can apply through our website, submit your resume to our career portal, or contact us directly. We\'ll review your profile and match you with suitable opportunities.'
        },
        {
          question: 'Do you help with resume preparation?',
          answer: 'Yes, our career consultants can help you optimize your resume, prepare for interviews, and provide career guidance to improve your job search success.'
        },
        {
          question: 'What types of positions do you fill?',
          answer: 'We provide staff for various positions including housekeeping, office support, security, hospital staff, drivers, delivery personnel, accounting roles, plumbing works, event staffing, and many more. We serve both temporary and permanent staffing needs.'
        }
      ]
    },
    {
      category: 'Process & Support',
      questions: [
        {
          question: 'What is your recruitment process?',
          answer: 'Our process follows six steps: Requirement Understanding, Talent Sourcing, Screening & Shortlisting, Client Interview Coordination, Deployment & Onboarding, and Continuous Support—from clarifying your needs through sourcing, screening, interviews, deployment, and ongoing partnership.'
        },
        {
          question: 'Do you provide ongoing support after placement?',
          answer: 'Yes, we provide ongoing support to ensure successful placements. We check in regularly and offer assistance with any issues that may arise.'
        },
        {
          question: 'Can you help with contract negotiations?',
          answer: 'Our experienced team can assist with salary negotiations and contract discussions to ensure fair and competitive offers for all parties.'
        },
        {
          question: 'What makes you different from other staffing agencies?',
          answer: 'Our personalized approach, industry expertise, comprehensive screening process, and commitment to long-term relationships set us apart. We focus on quality matches over quick placements.'
        }
      ]
    }
  ]

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 via-primary-700 to-teal-700 text-white py-16 md:py-20">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Frequently Asked Questions</h1>
            <p className="text-lg md:text-xl text-primary-100">
              Find answers to common questions about our services and processes
            </p>
            <div className="w-20 h-1 bg-accent-500 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Get Your Questions Answered
            </h2>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto">
              Browse through our frequently asked questions organized by category
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {faqs.map((category, categoryIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
                className="mb-12"
              >
                <h3 className="text-2xl font-bold text-primary-800 mb-6 flex items-center">
                  <HelpCircle className="w-6 h-6 text-primary-600 mr-3" />
                  {category.category}
                </h3>

                <div className="space-y-4">
                  {category.questions.map((faq, questionIndex) => {
                    const globalIndex = categoryIndex * 100 + questionIndex
                    return (
                      <motion.div
                        key={questionIndex}
                        className="bg-white rounded-lg shadow-md overflow-hidden"
                      >
                        <button
                          onClick={() => toggleItem(globalIndex)}
                          className="w-full p-6 text-left flex items-center justify-between hover:bg-secondary-50 transition-colors"
                        >
                          <span className="text-lg font-semibold text-secondary-900">
                            {faq.question}
                          </span>
                          <div className="flex-shrink-0 ml-4">
                            {openItems[globalIndex] ? (
                              <Minus className="w-5 h-5 text-primary-600" />
                            ) : (
                              <Plus className="w-5 h-5 text-primary-600" />
                            )}
                          </div>
                        </button>

                        <AnimatePresence>
                          {openItems[globalIndex] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 pb-6 pt-2">
                                <p className="text-secondary-600 leading-relaxed">
                                  {faq.answer}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    )
                  })}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-padding bg-secondary-50">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-xl p-8 md:p-12"
            >
              <MessageCircle className="w-16 h-16 text-primary-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-secondary-900 mb-4">
                Still Have Questions?
              </h2>
              <p className="text-xl text-secondary-600 mb-8">
                Our team is here to help. Contact us for personalized assistance with your specific needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="btn-primary">
                  Contact Us
                </button>
                <button className="btn-secondary">
                  Schedule a Call
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FAQ
